import React from 'react';
import { AlertTriangle, Share2, X } from 'lucide-react';
import styles from './ListedDelistConfirmModal.module.css';

type Props = {
  /** true = share inventory; false = unshare inventory */
  targetShared: boolean;
  ticketCount: number;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/** Confirm before sharing or unsharing Listed inventory on Victory Live. */
export function ListedShareConfirmModal({
  targetShared,
  ticketCount,
  loading = false,
  onCancel,
  onConfirm,
}: Props) {
  const title = targetShared ? 'Share Inventory' : 'Unshare Inventory';
  const message = targetShared
    ? 'Are you sure you want to share this inventory?'
    : 'Are you sure you want to unshare this inventory?';
  const ticketLabel = ticketCount > 1 ? `${ticketCount} tickets` : 'this ticket';
  const Icon = targetShared ? Share2 : AlertTriangle;
  const iconColor = targetShared ? '#1d4ed8' : '#b91c1c';

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div
              className={styles.iconWrap}
              style={
                targetShared
                  ? { background: 'var(--listed-share-icon-bg, #eff6ff)' }
                  : undefined
              }
            >
              <Icon color={iconColor} size={18} />
            </div>
            <div className={styles.titleBlock}>
              <div className={styles.title}>{title}</div>
              <div className={styles.subtitle}>
                {loading
                  ? targetShared
                    ? 'Sharing on Victory Live…'
                    : 'Unsharing on Victory Live…'
                  : `Affects ${ticketLabel} — status stays Listed`}
              </div>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            disabled={loading}
            onClick={onCancel}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className={styles.processing} role="status" aria-live="polite">
            <div className={styles.spinner} aria-hidden="true" />
            <div className={styles.processingText}>
              <b>{targetShared ? 'Sharing inventory…' : 'Unsharing inventory…'}</b>
              <p className={styles.processingHint}>
                Do not close this window until it finishes.
              </p>
            </div>
          </div>
        ) : (
          <div
            className={styles.warning}
            style={
              targetShared
                ? { background: '#eff6ff', borderLeftColor: '#2563eb' }
                : undefined
            }
          >
            <Icon color={iconColor} size={18} />
            <div>
              <b>{message}</b>
              <p>
                {targetShared
                  ? 'This will share the listing on the selected Victory Live marketplaces.'
                  : 'This will remove the listing from all Victory Live marketplaces.'}
              </p>
            </div>
          </div>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} disabled={loading} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmBtn}
            disabled={loading}
            style={targetShared ? { background: '#2563eb' } : undefined}
            onClick={() => void onConfirm()}
          >
            {loading ? 'Working…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
